import type { ConversationMessage, Language } from '@hero/schema';
import { agentCopy } from './agent-copy';
import type { LanguageEvidence } from './language-evidence';
import { ACTIVE_LANGUAGE_CODES, languageRegistry } from './packs/registry';

/** Order the options appear in — the registry's. */
export const LANGUAGE_PICKER_OPTIONS: readonly Language[] = ACTIVE_LANGUAGE_CODES;

/** Each option titled in its own script — the reader may not read the others. */
export const LANGUAGE_PICKER_TITLES = Object.fromEntries(
  ACTIVE_LANGUAGE_CODES.map(code => [code, languageRegistry[code].name.native]),
) as Record<Language, string>;

/** One line in every active language: the thread has no language yet. */
export const LANGUAGE_PICKER_BODY = ACTIVE_LANGUAGE_CODES.map(code =>
  agentCopy(code, 'picker.choose_language'),
).join(' · ');

/**
 * Added once to the first Urdu reply after learner Arabic moved the
 * thread to Urdu. In Arabic, because the speaker chose Arabic words;
 * «لغة» is a {@link isChangeLanguageRequest} and re-sends the picker.
 */
export const ACCENT_LANGUAGE_OFFER = 'تفضّل العربية؟ اكتب «لغة»';

const BUTTON_ID_PREFIX = 'lang:';

export const languagePickerButtonId = (language: Language): string =>
  `${BUTTON_ID_PREFIX}${language}`;

/** `lang:ur` → `ur`; anything else → null. */
export const interpretLanguageButtonId = (id: string | undefined): Language | null => {
  if (!id?.startsWith(BUTTON_ID_PREFIX)) return null;
  const value = id.slice(BUTTON_ID_PREFIX.length);
  return (LANGUAGE_PICKER_OPTIONS as readonly string[]).includes(value)
    ? (value as Language)
    : null;
};

/**
 * Plain-text twin for channels without buttons (and the persisted
 * transcript): the body plus a numbered list, so «2» is an answer.
 */
export const languagePickerFallbackText = (): string =>
  [
    LANGUAGE_PICKER_BODY,
    ...LANGUAGE_PICKER_OPTIONS.map((l, i) => `${i + 1}. ${LANGUAGE_PICKER_TITLES[l]}`),
  ].join('\n');

/**
 * A picker bubble in the transcript — the widget in-app and on Cloud,
 * its plain-text twin on channels without buttons. It asks a question
 * but is not an answer: the hello intro still counts as the first reply.
 */
export const isLanguagePickerMessage = (
  message: Pick<ConversationMessage, 'role' | 'content'>,
): boolean =>
  message.role === 'assistant' &&
  message.content.blocks.some(
    b =>
      (b.kind === 'widget' && b.widget.type === 'language_picker') ||
      (b.kind === 'text' && b.text.startsWith(LANGUAGE_PICKER_BODY)),
  );

/** «2», «٢» (Arabic-Indic) or «۲» (Urdu) → 2; anything else → 0. */
const pickerDigit = (text: string): number => {
  if (!/^[1-9١-٩۱-۹]$/u.test(text)) return 0;
  const code = text.charCodeAt(0);
  if (code >= 0x0661 && code <= 0x0669) return code - 0x0660;
  if (code >= 0x06f1 && code <= 0x06f9) return code - 0x06f0;
  return Number(text);
};

/**
 * A typed answer to an open picker: «2», «٣», or the option title as
 * shown. Only meaningful while the picker is the last thing we asked —
 * a bare «1» is otherwise a quantity.
 */
export const interpretLanguagePickerReply = (text: string): Language | null => {
  const trimmed = text.trim().replace(/[.)\-–]+$/u, '').trim();
  const digit = pickerDigit(trimmed);
  if (digit) return LANGUAGE_PICKER_OPTIONS[digit - 1] ?? null;
  for (const language of LANGUAGE_PICKER_OPTIONS) {
    if (trimmed.toLowerCase() === LANGUAGE_PICKER_TITLES[language].toLowerCase()) return language;
  }
  return null;
};

const CHANGE_LANGUAGE_RE = [
  /^(?:change|switch)\s+(?:the\s+)?language$/i,
  /^(?:language|lang)$/i,
  /^(?:تغيير|غير|تغير|بدل|تبديل)\s+(?:ال)?لغة$/u,
  /^(?:ال)?لغة$/u,
  /^زبان\s+(?:تبدیل|بدل|بدلیں|بدلو)(?:\s+کریں)?$/u,
  /^(?:zaban|zuban)\s+(?:badlo|badlein|change)$/i,
];

/** «change language», «تغيير اللغة», «زبان تبدیل کریں» — re-send the picker. */
export const isChangeLanguageRequest = (text: string): boolean => {
  const trimmed = text.trim().replace(/[.!؟?،,]+$/u, '').trim();
  if (!trimmed || trimmed.length > 30) return false;
  return CHANGE_LANGUAGE_RE.some(re => re.test(trimmed));
};

export type LanguagePickerDecision = 'ask' | 'confident';

const STRONG: ReadonlySet<LanguageEvidence['strength']> = new Set([
  'typed_sentence',
  'voice_confident',
  'explicit_choice',
]);

/**
 * Whether a thread's first settled burst (texts, voice transcripts,
 * photos) says clearly which language to answer in.
 *
 * `ask` when nothing in it is strong («Hello», an emoji, a photo, a
 * lane, a name, an unsure voice note), or when strong signals disagree
 * (typed Arabic beside an Urdu voice note). `conflicting` carries
 * signals no single line shows — learner Arabic, an Urdu speaker heard
 * behind Arabic words — that argue against what was typed.
 */
export const shouldAskLanguage = (input: {
  evidence: readonly LanguageEvidence[];
  conflicting?: boolean;
}): LanguagePickerDecision => {
  if (input.evidence.some(e => e.strength === 'explicit_choice')) return 'confident';
  const strong = input.evidence.filter(e => e.language && STRONG.has(e.strength));
  if (strong.length === 0) return 'ask';
  if (new Set(strong.map(e => e.language)).size > 1) return 'ask';
  return input.conflicting ? 'ask' : 'confident';
};
